import React from 'react';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import localeConfig from '../../config/locale.config';

const SubmitConfirmation = ({ data, onPrevious, onSubmit, onSave, isSubmitting, colors, isPending, registrationStatus }) => {
    const renderVendorDetails = () => (
        <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Vendor Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div><span className="font-medium">Name:</span> {data.vendor?.vendor_name || 'Not provided'}</div>
                <div><span className="font-medium">Contact:</span> {data.vendor?.contact_number || 'Not provided'}</div>
                <div><span className="font-medium">Owner:</span> {data.vendor?.owner_name || 'Not provided'}</div>
                <div><span className="font-medium">GST:</span> {data.vendor?.gst_number || 'Not provided'}</div>
                <div className="md:col-span-2"><span className="font-medium">Address:</span> {data.vendor?.address?.address || 'Not provided'}</div>
            </div>
        </div>
    );

    const renderLocations = () => (
        <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Vendor Locations ({data.locations?.length || 0})</h4>
            {data.locations && data.locations.length > 0 ? (
                <div className="space-y-2">
                    {data.locations.map((location, index) => (
                        <div key={index} className="text-xs border-l-2 border-blue-500 pl-2">
                            <div className="font-medium">Location {index + 1}</div>
                            <div className="text-gray-600">{location.location_address?.address || 'No address'}</div>
                            <div className="text-gray-600">Manager: {location.manager_name} - {location.manager_mobile}</div>
                            {location.is_primary && (
                                <span className="inline-block px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded mt-1">
                                    Primary
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-xs text-gray-500">No locations added</div>
            )}
        </div>
    );

    const renderServiceRates = () => (
        <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Service Rates ({data.serviceRates?.length || 0})</h4>
            {data.serviceRates && data.serviceRates.length > 0 ? (
                <div className="space-y-2">
                    {data.serviceRates.map((rate, index) => (
                        <div key={index} className="text-xs border-l-2 border-green-500 pl-2">
                            <div className="font-medium">
                                {rate.service_type_code?.toUpperCase()}
                                {rate.service_subtype_code && ` - ${rate.service_subtype_code}`}
                            </div>
                            <div className="text-gray-600">
                                Base Rate: {localeConfig.currency.symbol}{rate.base_service_rate} | Max KMs: {rate.base_max_kms} | 
                                Additional: {localeConfig.currency.symbol}{rate.additional_rate_per_km}/km
                            </div>
                            <div className="text-gray-600">
                                Time Slot: {rate.time_slot} ({rate.time_slot_start} - {rate.time_slot_end})
                            </div>
                            {rate.fixed_rate && (
                                <div className="text-gray-600">Fixed Rate: {localeConfig.currency.symbol}{rate.fixed_rate}</div>
                            )}
                            <div className="text-gray-600">
                                Cancel Charges: {localeConfig.currency.symbol}{rate.cancel_charges_per_km_upto_base_kms} (upto base) | 
                                {localeConfig.currency.symbol}{rate.cancel_charges_per_km_above_base_kms} (above base)
                            </div>
                            <div className="text-gray-600">
                                Effective: {rate.effective_from} {rate.effective_to && `to ${rate.effective_to}`}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-xs text-gray-500">No service rates added</div>
            )}
        </div>
    );

    const renderDrivers = () => (
        <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Vendor Drivers ({data.drivers?.length || 0})</h4>
            {data.drivers && data.drivers.length > 0 ? (
                <div className="space-y-2">
                    {data.drivers.map((driver, index) => (
                        <div key={index} className="text-xs border-l-2 border-purple-500 pl-2">
                            <div className="font-medium">{driver.name || 'No name'}</div>
                            <div className="text-gray-600">Mobile: {driver.mobile_number || 'No mobile'}</div>
                            <div className="text-gray-600">License: {driver.license_number || 'No license'}</div>
                            {driver.vehicle_number && (
                                <div className="text-gray-600">Vehicle: {driver.vehicle_number}</div>
                            )}
                            {driver.experience_years && (
                                <div className="text-gray-600">Experience: {driver.experience_years} years</div>
                            )}
                            {driver.license_expiry && (
                                <div className="text-gray-600">License Expiry: {driver.license_expiry}</div>
                            )}
                            {driver.vendor_location_id !== undefined && data.locations && (
                                <div className="text-gray-600">
                                    Location: {data.locations[driver.vendor_location_id] ? 
                                        `Location ${parseInt(driver.vendor_location_id) + 1}` : 'No location'}
                                </div>
                            )}
                            {!driver.is_active && (
                                <span className="inline-block px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded mt-1">
                                    Inactive
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-xs text-gray-500">No drivers added (optional)</div>
            )}
        </div>
    );

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                    {isPending ? 'Registration Summary' : 'Review & Submit'}
                </h2>
                <p className="text-sm text-gray-600">
                    {isPending 
                        ? 'Your vendor registration has been submitted and is currently under review.'
                        : 'Please review all the information before submitting your vendor registration.'
                    }
                </p>
            </div>

            <div className="space-y-4 mb-6">
                {renderVendorDetails()}
                {renderLocations()}
                {renderServiceRates()}
                {renderDrivers()}
            </div>

            {!isPending && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                    <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                            <h4 className="text-sm font-medium text-blue-900">Ready to Submit</h4>
                            <p className="text-xs text-blue-700 mt-1">
                                By clicking "Submit Registration", you confirm that all the information provided is accurate and complete. 
                                Your registration will be reviewed by our team and you will be notified once approved.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {isPending && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                    <div className="flex items-start">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mt-0.5 mr-2 flex-shrink-0"></div>
                        <div>
                            <h4 className="text-sm font-medium text-yellow-900">Registration Under Review</h4>
                            <p className="text-xs text-yellow-700 mt-1">
                                Your vendor registration is currently being reviewed by our team. You will be notified via email/SMS once the review is complete. 
                                You cannot make any changes while the registration is pending approval.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation Buttons */}
            {!isPending && (
                <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onPrevious}
                        disabled={isSubmitting}
                        className="flex items-center justify-center space-x-2 px-3 py-2 sm:py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span>Previous</span>
                    </button>

                    <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                        <button
                            type="button"
                            onClick={() => onSave(data)}
                            disabled={isSubmitting}
                            className="flex items-center justify-center space-x-2 px-3 py-2 sm:py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span>Save & Exit</span>
                        </button>

                        <button
                            type="button"
                            onClick={onSubmit}
                            disabled={isSubmitting}
                            className="flex items-center justify-center space-x-2 w-full sm:w-auto px-4 py-2 sm:py-1.5 text-sm text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            <span>{isSubmitting ? 'Submitting...' : 'Submit Registration'}</span>
                            <CheckCircle className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}

            {isPending && (
                <div className="flex justify-center pt-4 border-t">
                    <button
                        type="button"
                        onClick={() => onSave(data)}
                        className="flex items-center space-x-2 px-4 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                    >
                        <span>Back to Dashboard</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default SubmitConfirmation; 